
import { Package, Loader2, AlertCircle } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AppSearchInput } from "./AppSearchInput";
import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface LevelAppsAllAppsContentProps {
  levelId: string;
  appFilter?: any;
  allowedApps?: string[];
  blockedApps?: string[];
}

export const LevelAppsAllAppsContent = ({ levelId, appFilter, allowedApps, blockedApps }: LevelAppsAllAppsContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  // Use allApps API instead of inLevel to avoid backend stream merge issues
  // Then filter on client side based on level configuration
  const allAppsData = useQuery(api.apps.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 1000 // Get more apps to ensure we have all available
    }
  });

  const isLoading = allAppsData === undefined;
  const hasError = allAppsData === null;
  
  // Filter apps based on level's allowed/blocked configuration
  const apps = useMemo(() => {
    if (!allAppsData?.page) return [];
    
    const allApps = allAppsData.page;
    
    // If we have specific allowed apps, only show those
    if (allowedApps && allowedApps.length > 0) {
      return allApps.filter((app: any) => allowedApps.includes(app._id));
    }
    
    // If we have blocked apps, exclude those
    if (blockedApps && blockedApps.length > 0) {
      return allApps.filter((app: any) => !blockedApps.includes(app._id));
    }
    
    // Otherwise show all apps
    return allApps;
  }, [allAppsData?.page, allowedApps, blockedApps]);

  // Only filter apps based on search query - blacklist filtering handled by API
  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return apps;
    
    const query = searchQuery.toLowerCase();
    return apps.filter((app: any) => 
      app.name?.toLowerCase().includes(query) ||
      app.bundleId?.toLowerCase().includes(query) ||
      app.category?.toLowerCase().includes(query) ||
      app.tags?.some((tag: string) => tag.toLowerCase().includes(query))
    );
  }, [apps, searchQuery]);

  console.log('LevelAppsAllAppsContent - levelId:', levelId);
  console.log('LevelAppsAllAppsContent - apps loaded:', apps.length);
  console.log('LevelAppsAllAppsContent - blocked apps should be filtered by API:', blockedApps);
  console.log('LevelAppsAllAppsContent - filtered apps:', filteredApps.length);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
          <p className="text-sm text-gray-500">Loading apps...</p>
        </CardContent>
      </Card>
    );
  }

  if (hasError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Apps</h3>
          <p className="text-gray-600 text-center mb-4 max-w-md">
            There was an issue loading apps for this level. This might be because the level has no apps configured.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!apps || apps.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apps Available</h3>
          <p className="text-gray-600 text-center mb-4 max-w-md">
            No apps are currently available for this level.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Available Apps ({filteredApps.length} of {apps.length})
          </h3>
          <p className="text-sm text-gray-600">
            Apps available for this level (blacklisted apps filtered by API)
          </p>
        </div>
      </div>

      {/* Search input */}
      <div className="w-full max-w-md">
        <AppSearchInput
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onCancel={() => setSearchQuery("")}
          placeholder="Search apps in this level..."
        />
      </div>

      {/* Scrollable table container */}
      <div className="flex-1 border rounded-lg overflow-hidden">
        <ScrollArea className="h-[calc(100vh-400px)]">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 border-b">
              <TableRow className="h-10">
                <TableHead className="w-12 px-3 py-2">Icon</TableHead>
                <TableHead className="w-64 px-3 py-2">App Name</TableHead>
                <TableHead className="w-32 px-3 py-2">Category</TableHead>
                <TableHead className="px-3 py-2">Tags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApps.map((app: any) => (
                <TableRow key={app._id} className="h-12">
                  <TableCell className="px-3 py-2">
                    <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center">
                      {app.iconUrl ? (
                        <img 
                          src={app.iconUrl} 
                          alt={app.name}
                          className="w-8 h-8 rounded"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : (
                        <Package className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="font-medium text-gray-900 truncate">{app.name}</div>
                    <div className="text-xs text-gray-500 truncate">{app.bundleId}</div>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <span className="text-sm text-gray-600 truncate">
                      {app.category || "No category"}
                    </span>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    {app.tags && app.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {app.tags.slice(0, 2).map((tag: string, index: number) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {app.tags.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{app.tags.length - 2} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No tags</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredApps.length === 0 && searchQuery && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No apps found matching "{searchQuery}"
                  </TableCell>
                </TableRow>
              )}
              {filteredApps.length === 0 && !searchQuery && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No apps available for this level
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  );
};
